"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, PhoneCall, Wrench, Award, CircleCheck as CheckCircle2, Clock, Flame, Droplets, ArrowRight, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const trustGuarantees = [{
  title: "Preserves Manufacturer Warranties",
  description: "Every repair adheres strictly to factory specs to keep your active coverage valid."
}, {
  title: "Stocked OEM Replacement Parts",
  description: "Our emergency vans carry genuine manufacturer valves, igniters, and cartridges."
}, {
  title: "24/7 Clayton County Dispatch",
  description: "Rapid on-scene arrival in Jonesboro for burst water heaters and fixture failures."
}];
const brandDirectory = [{
  name: "Rheem",
  category: "Water Heaters",
  to: "/rheem-water-heater-service/",
  summary: "Tank & tankless diagnostic, burner assembly service, heating elements, and OEM replacements.",
  iconType: "flame",
  highlight: "Tank & Tankless"
}, {
  name: "Bradford White",
  category: "Water Heaters",
  to: "/bradford-white-water-heater-service/",
  summary: "Hydrojet sedimentation clearing, pressure-relief valves, gas control valves, and tank repairs.",
  iconType: "flame",
  highlight: "Commercial & Residential"
}, {
  name: "A. O. Smith",
  category: "Water Heaters",
  to: "/a-o-smith-water-heater-service/",
  summary: "High-efficiency condensing, hybrid heat pumps, thermostat calibrations, and tank replacements.",
  iconType: "flame",
  highlight: "Hybrid & Standard"
}, {
  name: "Navien",
  category: "Water Heaters",
  to: "/navien-water-heater-service/",
  summary: "Condensing tankless flow sensor diagnostics, dual heat exchanger maintenance, and error clearing.",
  iconType: "flame",
  highlight: "Tankless Specialist"
}, {
  name: "Rinnai",
  category: "Water Heaters",
  to: "/rinnai-water-heater-service/",
  summary: "Digital controller error diagnostics, descaling system flushes, and high-efficiency burner repairs.",
  iconType: "flame",
  highlight: "Tankless Certified"
}, {
  name: "Moen",
  category: "Faucets & Sinks",
  to: "/moen-faucet-sink-repair/",
  summary: "Duralast 1225/1222 cartridge rebuilds, touchless sensor diagnostics, and high-pressure supply lines.",
  iconType: "droplet",
  highlight: "OEM Cartridges"
}, {
  name: "Delta",
  category: "Faucets & Sinks",
  to: "/delta-faucet-sink-repair/",
  summary: "Diamond Seal Technology repairs, ball valve assemblies, pull-down hose fixes, and aerator service.",
  iconType: "droplet",
  highlight: "Diamond Seal Tech"
}, {
  name: "Kohler",
  category: "Toilets",
  to: "/kohler-toilet-repair/",
  summary: "Class Five canister flush valve rebuilds, silent fill valve upgrades, and leak-proof bowl reseating.",
  iconType: "shield",
  highlight: "High-Performance"
}, {
  name: "American Standard",
  category: "Toilets",
  to: "/american-standard-toilet-repair/",
  summary: "Champion 4 flush piston replacements, VorMax dual-flush servicing, and emergency clog clearing.",
  iconType: "shield",
  highlight: "Clog-Free Tech"
}];
export default function Expertise() {
  const shouldReduceMotion = useReducedMotion();
  const animationProps = shouldReduceMotion ? {
    initial: {
      opacity: 1
    },
    animate: {
      opacity: 1
    }
  } : {
    initial: {
      opacity: 0,
      y: 16
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      amount: 0.15
    },
    transition: {
      duration: 0.4,
      ease: [0.16, 1, 0.3, 1]
    }
  };
  const getBrandIcon = (iconType: string) => {
    switch (iconType) {
      case "flame":
        return <Flame className="w-5 h-5 text-accent" />;
      case "droplet":
        return <Droplets className="w-5 h-5 text-primary" />;
      case "shield":
      default:
        return <ShieldCheck className="w-5 h-5 text-primary" />;
    }
  };
  return <section id="brand-directory-hero" data-nav="light" className="bg-background text-foreground py-14 md:py-20 lg:py-24 border-b border-border">
      <div className="mx-auto max-w-[1240px] px-5 md:px-8">
        {/* Top 8/4 Hero Split */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start mb-16 md:mb-20">
          {/* Left Column (8 cols) */}
          <motion.div {...animationProps} className="lg:col-span-8 flex flex-col justify-center">
            {/* Eyebrow Pill */}
            <div className="inline-flex items-center gap-2 self-start rounded-full bg-secondary px-3.5 py-1.5 text-xs md:text-sm font-bold uppercase tracking-wider text-secondary-foreground mb-4">
              <Sparkles className="w-4 h-4 text-accent" />
              <span>Factory-Aligned Plumbing Specialists</span>
            </div>

            <h1 className="font-default text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-foreground leading-tight mb-5">
              Plumbing Brands We Service in Jonesboro, GA
            </h1>

            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-8 max-w-3xl">
              From continuous-flow tankless systems and high-recovery water heaters to luxury fixtures and commercial-grade toilets, our licensed Jonesboro technicians provide precision diagnostic repairs without voiding manufacturer warranties.
            </p>

            {/* Dual Action Response Cluster */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5 mb-8">
              <a href="tel:4708479076" className="w-full sm:w-auto">
                <Button size="lg" className="w-full sm:w-auto rounded-full bg-accent text-accent-foreground font-bold shadow-md hover:brightness-105 transition-[filter,transform] active:scale-95">
                  <PhoneCall className="w-5 h-5 mr-1" />
                  <span>Call (470) 847-9076</span>
                </Button>
              </a>

              <Button asChild variant="default" size="lg" className="w-full sm:w-auto rounded-full bg-primary text-primary-foreground font-semibold shadow-sm hover:bg-primary/90 transition-[background-color,transform] active:scale-95">
                <Link to="/contact/">
                  <span>Request Brand Service</span>
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
            </div>

            {/* Micro Feature Proof Strip */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-2 border-t border-border/80">
              <div className="flex items-center gap-2 text-xs sm:text-sm text-foreground font-medium">
                <CheckCircle2 className="w-4 h-4 text-accent shrink-0" />
                <span>Genuine OEM Parts</span>
              </div>
              <div className="flex items-center gap-2 text-xs sm:text-sm text-foreground font-medium">
                <Clock className="w-4 h-4 text-accent shrink-0" />
                <span>Same-Day Availability</span>
              </div>
              <div className="col-span-2 sm:col-span-1 flex items-center gap-2 text-xs sm:text-sm text-foreground font-medium">
                <Award className="w-4 h-4 text-accent shrink-0" />
                <span>Licensed GA Plumbers</span>
              </div>
            </div>
          </motion.div>

          {/* Right Column (4 cols): Credential Card */}
          <motion.div {...animationProps} transition={{
          ...animationProps.transition,
          delay: 0.1
        }} className="lg:col-span-4">
            <div className="rounded-2xl bg-card border border-border p-6 shadow-md relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-accent" />
              
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-lg bg-secondary text-primary flex items-center justify-center shrink-0">
                  <Award className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="font-default text-base sm:text-lg font-bold text-card-foreground">
                    Warranty-Safe Guarantee
                  </h2>
                  <p className="text-xs text-muted-foreground">Certified Georgia Trade Standards</p>
                </div>
              </div>

              <div className="space-y-4 mb-6">
                {trustGuarantees.map((item, idx) => <div key={item.title} data-index={idx} className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-default text-xs sm:text-sm font-bold text-card-foreground">
                        {item.title}
                      </h3>
                      <p className="text-xs text-muted-foreground leading-normal mt-0.5">
                        {item.description}
                      </p>
                    </div>
                  </div>)}
              </div>

              <div className="p-3.5 rounded-xl bg-background border border-border flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Wrench className="w-4 h-4 text-accent" />
                  <span className="text-xs font-bold text-foreground">Diagnostic Van Dispatch</span>
                </div>
                <span className="text-xs font-semibold text-primary">Jonesboro, GA</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Brand Directory Grid Header */}
        <div className="mb-8 md:mb-12 flex flex-col md:flex-row md:items-end justify-between gap-4 border-t border-border pt-10">
          <div>
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
              <span>Primary Manufacturer Coverage</span>
            </div>
            <h2 className="font-default text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
              Explore Serviced Brands & Systems
            </h2>
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground max-w-md">
            Select your installed manufacturer below to review specialized repair solutions, cartridge replacements, or tankless flush maintenance.
          </p>
        </div>

        {/* 3-Column Responsive Brand Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {brandDirectory.map((brand, index) => <motion.div key={brand.name} data-index={index} {...animationProps} transition={{
          ...animationProps.transition,
          delay: index * 0.04
        }} className="group rounded-xl bg-card border border-border p-6 shadow-xs hover:border-primary/50 hover:bg-background transition-[background-color,border-color,box-shadow] duration-200 flex flex-col justify-between">
              <div>
                {/* Card Top: Icon Pod + Category Tag */}
                <div className="flex items-center justify-between gap-3 mb-4">
                  <div className="w-12 h-12 rounded-lg bg-secondary flex items-center justify-center shrink-0 group-hover:bg-primary/10 transition-colors">
                    {getBrandIcon(brand.iconType)}
                  </div>
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-bold tracking-wide">
                    {brand.category}
                  </span>
                </div>

                {/* Brand Name & Highlight Badge */}
                <div className="flex items-baseline gap-2 mb-2">
                  <h3 className="font-default text-xl font-bold text-card-foreground group-hover:text-primary transition-colors">
                    {brand.name}
                  </h3>
                  <span className="text-xs text-muted-foreground font-medium">
                    • {brand.highlight}
                  </span>
                </div>

                {/* Technical Diagnostic Summary */}
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed mb-6">
                  {brand.summary}
                </p>
              </div>

              {/* Direct Link Action */}
              <div className="pt-4 border-t border-border/70 flex items-center justify-between">
                <Link to={brand.to} className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold text-primary group-hover:text-accent transition-colors">
                  <span>View Service Coverage</span>
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Link>
                <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                  Verified Spec
                </span>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}